import { ApiAuthNetworkContextCompatibleResSchema } from "@bridge-applications/pb-types";
import { useSlice } from "@bridge-applications/provider-kit";
import { useEffect, useRef } from "react";
import { POSTHOG_ENABLED } from "../../state/config";
import { authenticatedApiCall } from "../helpers/api";
import { countryAnalyticsCache } from "../helpers/countryAnalyticsContext";
import {
    applyNetworkContextBootstrapState,
    browserNetworkContextStorage,
    storeResolvedNetworkContext,
} from "../helpers/networkAnalyticsContext";
import {
    ANALYTICS_CONTEXT_PATH,
    requestAnalyticsContext,
} from "./analyticsContextRequest";

export function useAnalyticsContext(): void {
    const { state: auth } = useSlice("authSlice");
    const { setSlice: setAnalytics } = useSlice("analyticsContextSlice");
    const priorUser = useRef<string | null>(null);

    useEffect(() => {
        if (
            !POSTHOG_ENABLED ||
            auth.bootstrapStatus !== "ready" ||
            !auth.userId
        ) {
            if (priorUser.current) {
                countryAnalyticsCache.clear();
                priorUser.current = null;
                setAnalytics({
                    userId: null,
                    status: "idle",
                    countryContext: null,
                });
            }
            return;
        }
        const userId = auth.userId;
        priorUser.current = userId;
        const telegramUserId = auth.telegramUserId;
        const storage = browserNetworkContextStorage();
        const controller = new AbortController();
        countryAnalyticsCache.confirm(userId, telegramUserId);
        applyNetworkContextBootstrapState(
            storage,
            telegramUserId,
            auth.networkContextState ?? undefined,
        );
        setAnalytics({ userId, status: "loading", countryContext: null });

        function wait(milliseconds: number): Promise<void> {
            if (controller.signal.aborted) return Promise.resolve();
            return new Promise((resolve) => {
                const finish = () => {
                    window.clearTimeout(timer);
                    controller.signal.removeEventListener("abort", finish);
                    resolve();
                };
                const timer = window.setTimeout(finish, milliseconds);
                controller.signal.addEventListener("abort", finish, {
                    once: true,
                });
            });
        }

        void requestAnalyticsContext({
            fetch: async () =>
                (
                    await authenticatedApiCall(
                        ANALYTICS_CONTEXT_PATH,
                        ApiAuthNetworkContextCompatibleResSchema,
                        {
                            signal: controller.signal,
                            cache: "no-store",
                        },
                    )
                ).body,
            wait,
            isCurrent: () => !controller.signal.aborted,
            apply: (response) => {
                if (response.status === "ERROR") {
                    setAnalytics({ status: "unavailable" });
                    return;
                }
                const network = response.networkContext;
                if (network.state === "RESOLVED" && telegramUserId !== null) {
                    storeResolvedNetworkContext(storage, {
                        userId,
                        telegramUserId,
                        token: network.token,
                        context: network.context,
                    });
                } else if (network.state === "UNAVAILABLE") {
                    applyNetworkContextBootstrapState(
                        storage,
                        telegramUserId,
                        "UNAVAILABLE",
                    );
                }
                const country =
                    "countryContext" in response
                        ? response.countryContext
                        : { state: "UNAVAILABLE" as const };
                if (
                    country.state === "RESOLVED" &&
                    !countryAnalyticsCache.store(
                        userId,
                        telegramUserId,
                        country.estimate,
                    )
                )
                    return;
                setAnalytics({
                    userId,
                    status:
                        country.state === "PENDING"
                            ? "loading"
                            : country.state === "RESOLVED"
                              ? "ready"
                              : "unavailable",
                    countryContext: country,
                });
            },
        })
            .then(() => {
                if (!controller.signal.aborted)
                    setAnalytics((current) =>
                        current.status === "loading"
                            ? { ...current, status: "unavailable" }
                            : current,
                    );
            })
            .catch(() => {
                if (!controller.signal.aborted)
                    setAnalytics({ status: "unavailable" });
            });

        return () => {
            controller.abort();
            // In-flight responses are ignored. A later identity must reconfirm before storing.
        };
    }, [
        auth.bootstrapStatus,
        auth.userId,
        auth.telegramUserId,
        auth.networkContextState,
        setAnalytics,
    ]);
}
